import { Extension } from '@tiptap/core';
import { PluginKey } from '@tiptap/pm/state';
import Suggestion from '@tiptap/suggestion';
import type {SuggestionKeyDownProps, SuggestionProps} from '@tiptap/suggestion';

export interface SuggestionMatch {
  token: string;
  clientRect: (() => DOMRect | null) | null;
  apply: (completion: string, shouldReplace: boolean) => void;
}

interface SuggestionTriggerOptions {
  char: string;
  onOpen: (match: SuggestionMatch) => void;
  onClose: () => void;
  onKeyDown: (props: SuggestionKeyDownProps) => boolean;
}

// Detects an "@handle", "#hashtag" or ":shortcode" token being typed at the
// cursor and reports it upward; unlike Tiptap's usual Mention-style usage,
// selecting a suggestion inserts plain text (not a custom node), since the
// backend mention/hashtag pipeline already expects plain "@handle"/"#tag"
// text and knows nothing about rich-text node types.
export function createSuggestionTrigger(name: string, char: string) {
  return Extension.create<SuggestionTriggerOptions>({
    name,

    addOptions() {
      return {
        char,
        onOpen: () => {
          /* noop */
        },
        onClose: () => {
          /* noop */
        },
        onKeyDown: () => false,
      };
    },

    addProseMirrorPlugins() {
      const { char: triggerChar, onOpen, onClose, onKeyDown } = this.options;

      const report = (props: SuggestionProps) => {
        onOpen({
          token: triggerChar + props.query,
          clientRect: props.clientRect ?? null,
          apply: (completion: string, shouldReplace: boolean) => {
            props.command({ completion, shouldReplace });
          },
        });
      };

      return [
        Suggestion({
          editor: this.editor,
          char: triggerChar,
          allowedPrefixes: null,
          pluginKey: new PluginKey(`suggestion-${name}`),
          items: () => [],
          command: ({ editor, range, props }) => {
            const { completion, shouldReplace } = props as {
              completion: string;
              shouldReplace: boolean;
            };

            if (shouldReplace) {
              editor.chain().focus().insertContentAt(range, `${completion} `).run();
            } else {
              editor.chain().focus().insertContentAt(range.to, ' ').run();
            }
          },
          render: () => ({
            onStart: report,
            onUpdate: report,
            onExit: onClose,
            onKeyDown,
          }),
        }),
      ];
    },
  });
}
